import { useEffect, useState } from "react";
import { api, ApiError, type Kohde } from "./api";
import { useKohdeSynkka } from "./Kohdekonteksti";
import { useLataus, LatausTila } from "./Lataus";
import { LinjaLinkki } from "./Linkit";
import { Tilasana } from "./Tila";
import LinjatarkastusRaportti from "./LinjatarkastusRaportti";
import { naytaPvm } from "./pvm";
import { t, virheTeksti } from "./kieli";

/* T646 (1) (Toni 21.10.2026 klo 10.12, järjestys 23.10.2026 klo 05.35): linjatarkastukset kohteittain — hyväksytty / kesken /
   aloittamatta, hyväksytyistä T635:n linjatarkastusraportti. Seuranta ja raportointi -ryhmässä; tilaajalle kiinni. */
type Tila = "hyvaksytty" | "kesken" | "aloittamatta";
type Rivi = Awaited<ReturnType<typeof api.linjatarkastusSeuranta>>["rivit"][number];

export default function Linjatarkastukset() {
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [kohde, setKohde] = useState<number | "">("");
  const { ylapalkissa, ylaValittu } = useKohdeSynkka(kohde, setKohde, { kaikkiSallittu: false });
  const [tila, setTila] = useState<Tila | "">("");
  const [data, setData] = useState<{ kohde: number; rivit: Rivi[]; maarat: Record<Tila, number> } | null>(null);
  const [viesti, setViesti] = useState<string | null>(null);
  const [raportti, setRaportti] = useState<number | null>(null);
  const kl = useLataus();
  useEffect(() => { kl.seuraa(api.kohteet().then((k) => { setKohteet(k); setKohde((v) => v === "" && k.length ? (k.find((x) => (x.linjoja ?? 0) > 0) ?? k[0]!).id : v); })); }, [kl.kerta]);
  useEffect(() => {
    if (kohde === "") return;
    let elossa = true;
    setViesti(null);
    api.linjatarkastusSeuranta({ kohde, tila: tila || undefined })
      .then((d) => { if (elossa) setData({ ...d, kohde }); })
      .catch((e) => { if (elossa) setViesti(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("seur.haku_virhe")); });
    return () => { elossa = false; };
  }, [kohde, tila]);

  const sana = (x: Tila) => x === "hyvaksytty" ? <Tilasana laji="kunnossa">{t("seur.lt.hyvaksytty")}</Tilasana>
    : x === "kesken" ? <Tilasana laji="varoitus">{t("seur.lt.kesken")}</Tilasana> : <Tilasana laji="ei_mitattu">{t("seur.lt.aloittamatta")}</Tilasana>;
  return (
    <section className="panel" data-nakyma="linjatarkastukset" data-kohde={data?.kohde ?? ""}>
      {raportti != null && <LinjatarkastusRaportti linjaId={raportti} sulje={() => setRaportti(null)} />}
      <div className="panel__head"><h2>{t("valikko.linjatarkastukset")}</h2></div>
      <LatausTila l={kl} nimi="lataus.kohteet" />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        {!(ylapalkissa && ylaValittu != null) && <label className="dim">{t("kuittaus.kohde")}<br />
          <select value={kohde} onChange={(e) => setKohde(Number(e.target.value))}>
            {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
          </select></label>}
        <label className="dim">{t("seur.tila")}<br />
          <select value={tila} onChange={(e) => setTila(e.target.value as Tila | "")} data-suodatin="lt-tila">
            <option value="">{t("nippu.kaikki")}</option>
            <option value="hyvaksytty">{t("seur.lt.hyvaksytty")}</option>
            <option value="kesken">{t("seur.lt.kesken")}</option>
            <option value="aloittamatta">{t("seur.lt.aloittamatta")}</option>
          </select></label>
      </div>
      {data && (
        <p style={{ margin: "8px 0" }} data-lt-maarat={`${data.maarat.hyvaksytty}|${data.maarat.kesken}|${data.maarat.aloittamatta}`}>
          {t("seur.lt.maarat", { h: data.maarat.hyvaksytty, k: data.maarat.kesken, a: data.maarat.aloittamatta })}
        </p>
      )}
      {viesti && <p className="dim" role="status">{viesti}</p>}
      <div className="scroll">
        <table className="tbl" style={{ fontSize: 13 }} data-rivit={data?.rivit.length ?? 0} data-joukko="linjatarkastukset">
          <thead><tr><th>{t("nippu.linja")}</th><th>{t("seur.tila")}</th><th>{t("ltr.paiva")}</th><th>{t("ltr.tarkastaja")}</th>
            <th>{t("seur.hyvaksyja")}</th><th>{t("seur.avoimet_punchit")}</th><th></th></tr></thead>
          <tbody>{(data?.rivit ?? []).map((r) => (
            <tr key={r.linja_id} data-lt-linja={r.tunnus} data-lt-tila={r.tila}>
              <td className="mono"><LinjaLinkki tunnus={r.tunnus} /></td>
              <td>{sana(r.tila)}{r.tuotu && <span className="dim"> · {t("seur.tuotu")}</span>}</td>
              <td>{r.pvm ? naytaPvm(r.pvm) : "–"}</td>
              <td>{r.tarkastaja ?? "–"}</td>
              <td>{r.hyvaksyja ?? (r.tila === "hyvaksytty" && r.tuotu ? t("ltar.lahteen_poytakirja") : "–")}</td>
              <td>{r.avoimia_puncheja}{r.avoimia_a_puncheja > 0 && <b> · A {r.avoimia_a_puncheja}</b>}</td>
              <td>{r.tila === "hyvaksytty" && (
                <button type="button" className="nappi nappi--paa" data-lt-raportti={r.tunnus} onClick={() => setRaportti(r.linja_id)}>{t("seur.raportti")}</button>
              )}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </section>
  );
}
